import React, { useMemo, useState } from 'react';
import {
  Card,
  DatePicker,
  Select,
  Table,
  Row,
  Col,
  Statistic,
  Switch,
  Space,
  Button,
} from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const { RangePicker } = DatePicker;

// Computes standard daily duration for a single entry
const calculateDailyHours = (startTime, endTime, totalMinutes) => {
    const totalHOurs= totalMinutes / 60 || 0;
return totalHOurs;
};

const LeaveHoursReport = ({ leaves }) => {
  const [approvedOnly, setApprovedOnly] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState();
  const [dateRange, setDateRange] = useState([
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ]);

  // Extract unique employee option lists
  const employees = useMemo(() => {
    const map = new Map();
    leaves.forEach((x) => {
      map.set(x.staffId, {
        label: x.staffName,
        value: x.staffId,
      });
    });
    return [...map.values()];
  }, [leaves]);

  // Build rows mapping out dynamic calendar metrics
  const reportRows = useMemo(() => {
    if (!dateRange) return [];

    let filtered = [...leaves];

    // Filter global state options by Approved state and Dropdown selectors
    if (approvedOnly) {
      filtered = filtered.filter((x) => x.approvalStatus === 'approved');
    }
    if (selectedEmployee) {
      filtered = filtered.filter((x) => x.staffId === selectedEmployee);
    }

    const grouped = {};

    filtered.forEach((leave) => {
      const leaveStart = dayjs(leave.startDate, 'DD-MM-YYYY');
      const leaveEnd = dayjs(leave.endDate, 'DD-MM-YYYY');
      const hoursPerDay = calculateDailyHours(leave.starttime, leave.endtime,leave.totalMinutes);

      // Loop through all dates matching this leave item's date duration window
      let currentDay = leaveStart;
      while (currentDay.isBefore(leaveEnd.add(1, 'day'), 'day')) {
        // Double check day falls perfectly within our selected dashboard RangePicker view bounds
        if (
          currentDay.isAfter(dateRange[0].subtract(1, 'day'), 'day') &&
          currentDay.isBefore(dateRange[1].add(1, 'day'), 'day')
        ) {
          const dateStr = currentDay.format('DD-MM-YYYY');

          if (!grouped[leave.staffId]) {
            grouped[leave.staffId] = {
              key: leave.staffId,
              staffName: leave.staffName,
              totalHours: 0,
            };
          }

          grouped[leave.staffId][dateStr] = (grouped[leave.staffId][dateStr] || 0) + hoursPerDay;
          grouped[leave.staffId].totalHours += hoursPerDay;
        }
        currentDay = currentDay.add(1, 'day');
      }
    });

    return Object.values(grouped);
  }, [leaves, dateRange, selectedEmployee, approvedOnly]);

  const downloadLeaveReport = () => {
    if (!dateRange) return;

    const dates = [];
    let current = dateRange[0];

    while (current.isBefore(dateRange[1].add(1, 'day'), 'day')) {
      dates.push(current.format('DD-MM-YYYY'));
      current = current.add(1, 'day');
    }

    const headers = ['Employee', ...dates, 'Total Leave Hours'];
    const rows = reportRows.map((row) => {
      const daily = dates.map((d) => (row[d] ? Number(row[d]).toFixed(2) : '0.00'));
      return [
        row.staffName,
        ...daily,
        Number(row.totalHours || 0).toFixed(2),
      ];
    });

    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leave_report_${dateRange[0].format('YYYYMMDD')}_${dateRange[1].format('YYYYMMDD')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const dailyColumns = useMemo(() => {
    if (!dateRange) return [];

    const cols = [];
    let current = dateRange[0];

    while (current.isBefore(dateRange[1].add(1, 'day'), 'day')) {
      const dateKey = current.format('DD-MM-YYYY');
      cols.push({
        title: current.format('DD MMM'),
        dataIndex: dateKey,
        width: 100,
        render: (value) => (value ? value.toFixed(2) : ''),
      });
      current = current.add(1, 'day');
    }

    return cols;
  }, [dateRange]);

  const totalHours = reportRows.reduce((sum, row) => sum + row.totalHours, 0);

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card>
        <Row gutter={[16, 16]} align="middle">
          <Col>
            <RangePicker value={dateRange} onChange={setDateRange} />
          </Col>
          <Col>
            <Select
              allowClear
              style={{ width: 250 }}
              placeholder="Filter by Employee"
              value={selectedEmployee}
              onChange={setSelectedEmployee}
              options={employees}
            />
          </Col>
          <Col>
            <Space>
              Approved Only
              <Switch checked={approvedOnly} onChange={setApprovedOnly} />
            </Space>
          </Col>
          <Col>
            <Button type="primary" onClick={downloadLeaveReport}>
              Download Leave CSV
            </Button>
          </Col>
        </Row>
      </Card>

      <Row gutter={16}>
        <Col span={8}>
          <Card>
            <Statistic title="Total Leave Hours" value={totalHours.toFixed(2)} />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic title="Employees on Leave" value={reportRows.length} />
          </Card>
        </Col>
      </Row>

      <Card title="Daily Leave Allocation Breakdown">
        <Table
          bordered
          size="small"
          rowKey="key"
          pagination={false}
          scroll={{ x: 'max-content' }}
          dataSource={reportRows}
          columns={[
            {
              title: 'Employee',
              dataIndex: 'staffName',
              fixed: 'left',
              width: 200,
            },
            ...dailyColumns,
            {
              title: 'Total Hours',
              dataIndex: 'totalHours',
              fixed: 'right',
              width: 150,
              render: (value) => value.toFixed(2),
            },
          ]}
        />
      </Card>

      <Card title="Employee Leave Summary">
        <Table
          rowKey="key"
          pagination={false}
          dataSource={reportRows}
          columns={[
            { title: 'Employee', dataIndex: 'staffName' },
            { title: 'Total Leave Hours Assigned', dataIndex: 'totalHours', render: (val) => val.toFixed(2) },
          ]}
        />
      </Card>
    </Space>
  );
};

export default LeaveHoursReport;